import React, { useState, useEffect } from "react";
import { Table, Typography, Space, Input, message, Button, Form, Select, Card, Row, Col, Tag, Checkbox, Alert, Progress } from "antd";
import { SearchOutlined, ClearOutlined, DownloadOutlined } from '@ant-design/icons';
import AxiosOICStat from "AxiosOICStat";
import { useSelector } from 'react-redux';
import DatePicker from 'react-multi-date-picker';
import DatePanel from "react-multi-date-picker/plugins/date_panel"
import PresetValuesPlugin from '../datePickerPlugins/PresetValuesPlugin';

const { Text } = Typography;
const { Option } = Select;

export default function WdiConvertFetchTab({ onConvertSuccess, refreshTrigger }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 100, total: 0, totalPages: 0 });
  const [form] = Form.useForm();
  const [countryMappings, setCountryMappings] = useState([]);
  const [indicatorMappings, setIndicatorMappings] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [unavailableIndicators, setUnavailableIndicators] = useState([]);
  const countriesState = useSelector((state) => state.countries);
  const [indicatorSearchFilter, setIndicatorSearchFilter] = useState('');
  const [selectedYears, setSelectedYears] = useState([]);
  const [taskId, setTaskId] = useState(null);
  const [convertProgress, setConvertProgress] = useState(null);

  useEffect(() => {
    fetchCountryMappings();
    fetchIndicatorMappings();
  }, [refreshTrigger]);

  const fetchCountryMappings = async () => {
    try {
      const res = await AxiosOICStat.get("/external_api/wdi/country-mappings");
      setCountryMappings(res.data?.mappings || res.data || []);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed to load WDI country mappings.";
      message.error(detail);
    }
  };

  const fetchIndicatorMappings = async () => {
    try {
      const res = await AxiosOICStat.get("/external_api/wdi/indicator-mappings");
      const mappings = res.data?.mappings || res.data || [];
      setIndicatorMappings(mappings);
      setUnavailableIndicators(mappings.filter(m => m.status === false));
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed to load WDI indicator mappings.";
      message.error(detail);
    }
  };

  const exportToCsv = async () => {
    if (!taskId) {
      message.warning('No data to export');
      return;
    }
    setExportLoading(true);
    try {
      const res = await AxiosOICStat.get(`/external_api/wdi/convert/export/${taskId}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `wdi_export_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to export data';
      message.error(detail);
    } finally {
      setExportLoading(false);
    }
  };

  const fetchResultPage = async (id, page, pageSize) => {
    const res = await AxiosOICStat.get(`/external_api/wdi/convert/result/${id}`, { params: { page, page_size: pageSize } });
    setData(res.data.data || []);
    setPagination({
      current: res.data.page,
      pageSize: res.data.page_size,
      total: res.data.total,
      totalPages: res.data.total_pages
    });
    return res.data;
  };

  const waitForConvertTask = async (id) => {
    while (true) {
      const res = await AxiosOICStat.get(`/external_api/wdi/convert/status/${id}`);
      const { status, progress, total, error } = res.data;
      setConvertProgress({ progress, total });
      if (status === 'completed') return;
      if (status === 'error') throw new Error(error || 'Conversion failed on server');
      await new Promise(resolve => setTimeout(resolve, 1500));
    }
  };

  const handleSearch = async (values) => {
    if (!values.ind_codes || !values.ind_codes.length) {
      message.warning("Please select one or more OIC indicators");
      return;
    }

    // Calculate start_year and end_year from selectedYears
    let startYear = undefined;
    let endYear = undefined;
    if (selectedYears && selectedYears.length > 0) {
      const yearVals = [];
      selectedYears.forEach(range => {
        if (range.length === 2) {
          for (let i = range[0].year; i <= range[1].year; i++) {
            yearVals.push(i);
          }
        } else {
          yearVals.push(range[0].year);
        }
      });
      if (yearVals.length > 0) {
        startYear = Math.min(...yearVals);
        endYear = Math.max(...yearVals);
      }
    }

    setLoading(true);
    setTaskId(null);
    setConvertProgress({ progress: 0, total: values.ind_codes.length });
    try {
      const requestData = {
        ind_codes: values.ind_codes,
        c_code: values.c_code ?? undefined,
        start_year: startYear,
        end_year: endYear
      };

      const startRes = await AxiosOICStat.post("/external_api/wdi/convert/start", requestData);
      const id = startRes.data.task_id;
      await waitForConvertTask(id);
      setTaskId(id);
      const result = await fetchResultPage(id, 1, pagination.pageSize || 100);
      message.success(`Found ${result.total} records`);
      // Check for unavailable indicators
      const selectedCodes = values.ind_codes || [];
      const failedMappings = selectedCodes
        .map(code => indicatorMappings.find(m => m.oic_ind_code === code))
        .filter(m => m && m.status === false);
      setUnavailableIndicators(failedMappings);
      if (onConvertSuccess) onConvertSuccess();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed to convert/fetch WDI data.";
      message.error(detail);
      setData([]);
      setPagination({ current: 1, pageSize: 100, total: 0, totalPages: 0 });
    } finally {
      setLoading(false);
      setConvertProgress(null);
    }
  };

  const handleTableChange = async (newPagination) => {
    if (!taskId) return;

    setLoading(true);
    try {
      await fetchResultPage(taskId, newPagination.current, newPagination.pageSize);
      if (onConvertSuccess) onConvertSuccess();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Failed to fetch page. Please try again.";
      message.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: "Country", dataIndex: "country_name", key: "country_name", render: (val) => val ? <Text>{val}</Text> : <Text type="secondary">—</Text>, sorter: (a, b) => (String(a.country_name || '').localeCompare(String(b.country_name || ''))) },
    { title: "Indicator Code", dataIndex: "oic_ind_code", key: "oic_ind_code", render: (val) => <Text strong>{val}</Text>, sorter: (a, b) => (Number(a.oic_ind_code) || 0) - (Number(b.oic_ind_code) || 0) },
    { title: "WDI Code", dataIndex: "wdi_code", key: "wdi_code", render: (val) => val ? <Text code>{val}</Text> : <Text type="secondary">—</Text>, sorter: (a, b) => (a.wdi_code || '').localeCompare(b.wdi_code || '') },
    { title: "Year", dataIndex: "year", key: "year", render: (val) => val ? <Text>{val}</Text> : <Text type="secondary">—</Text>, sorter: (a, b) => (Number(a.year) || 0) - (Number(b.year) || 0) },
    { title: "WDI Value", dataIndex: "wdi_value", key: "wdi_value", render: (val) => val !== null && val !== undefined ? <Text>{val}</Text> : <Text type="secondary">—</Text>, sorter: (a, b) => (Number(a.wdi_value) || 0) - (Number(b.wdi_value) || 0) },
    { title: "OIC Value", dataIndex: "oic_value", key: "oic_value", render: (val) => val !== null && val !== undefined ? <Text>{val}</Text> : <Text type="secondary">—</Text>, sorter: (a, b) => (Number(a.oic_value) || 0) - (Number(b.oic_value) || 0) },
    { title: "Match", dataIndex: "match", key: "match", render: (val) => (<Tag color={val ? 'green' : 'red'}>{val ? 'Match' : 'No Match'}</Tag>), sorter: (a, b) => {
      const av = a.match ? 1 : 0;
      const bv = b.match ? 1 : 0;
      return bv - av; // put matches first
    } }
  ];

  const mappedCountries = (countryMappings || []).filter(m => m && m.c_code != null).sort((a, b) => {
    try { return Number(a.c_code) - Number(b.c_code); } catch (e) { return 0; }
  });

  // build unique available indicators from mappings
  const uniqueIndicators = new Map();
  (indicatorMappings || []).forEach(mapping => {
    const code = mapping.oic_ind_code;
    if (code != null && !uniqueIndicators.has(code)) {
      uniqueIndicators.set(code, {
        oic_ind_code: code,
        oic_ind_name: mapping.oic_ind_name
      });
    }
  });
  const availableIndicators = Array.from(uniqueIndicators.values()).sort((a, b) => {
    const aName = a.oic_ind_name || '';
    const bName = b.oic_ind_name || '';
    return aName.localeCompare(bName);
  });

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      {unavailableIndicators.length > 0 && (
        <Alert
          type="error"
          showIcon
          closable
          message={`${unavailableIndicators.length} indicator(s) unavailable in WDI API`}
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {unavailableIndicators.map(m => {
                const indInfo = availableIndicators.find(i => i.oic_ind_code === m.oic_ind_code);
                const displayName = indInfo?.oic_ind_name || m.oic_ind_name;
                return (
                  <li key={m.id}>
                    <Text code>{m.wdi_series_code}</Text>
                    {m.oic_ind_code ? ` (OIC: ${m.oic_ind_code})` : ''}
                    {displayName ? ` \u2014 ${displayName}` : ''}
                  </li>
                );
              })}
            </ul>
          }
          onClose={() => setUnavailableIndicators([])}
        />
      )}
      <Card>
        <Form form={form} layout="vertical" onFinish={handleSearch}>
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item name="ind_codes" label="OIC Indicators">
                  <Select
                    mode="multiple"
                      showSearch
                      allowClear
                      placeholder="Select indicators"
                      searchValue={indicatorSearchFilter}
                      onSearch={(val) => setIndicatorSearchFilter(val)}
                      autoClearSearchValue={false}
                    dropdownRender={(menu) => {
                      const filteredOptions = availableIndicators.filter(i => {
                        const searchText = `${i.oic_ind_code} ${i.oic_ind_name}`.toLowerCase();
                        return searchText.indexOf(indicatorSearchFilter.toLowerCase()) >= 0;
                      });
                      const selectedVals = form.getFieldValue("ind_codes") || [];
                      const allFilteredSelected = filteredOptions.length > 0 && filteredOptions.every(opt => selectedVals.includes(opt.oic_ind_code));
                      const someFilteredSelected = filteredOptions.some(opt => selectedVals.includes(opt.oic_ind_code));

                      return (
                        <>
                          <div style={{ padding: '8px', borderBottom: '1px solid #f0f0f0' }}>
                            <Checkbox
                              checked={allFilteredSelected}
                              indeterminate={someFilteredSelected && !allFilteredSelected}
                              onChange={(e) => {
                                const currentSelected = form.getFieldValue("ind_codes") || [];
                                const filteredCodes = filteredOptions.map(opt => opt.oic_ind_code);
                                if (e.target.checked) {
                                  form.setFieldsValue({
                                    ind_codes: [...new Set([...currentSelected, ...filteredCodes])]
                                  });
                                } else {
                                  form.setFieldsValue({
                                    ind_codes: currentSelected.filter(code => !filteredCodes.includes(code))
                                  });
                                }
                              }}
                            >
                              Select All in Results
                            </Checkbox>
                          </div>
                          {menu}
                        </>
                      );
                    }}
                    filterOption={(input, option) => {
                      const indicator = availableIndicators.find(i => i.oic_ind_code === option.value);
                      if (!indicator) return false;
                      const searchText = `${indicator.oic_ind_code} ${indicator.oic_ind_name}`.toLowerCase();
                      return searchText.indexOf(input.toLowerCase()) >= 0;
                    }}
                  >
                    {availableIndicators.map(indicator => (
                      <Option key={indicator.oic_ind_code} value={indicator.oic_ind_code}>
                        {indicator.oic_ind_code} - {indicator.oic_ind_name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="c_code" label="Country Filter (Optional)">
                <Select showSearch allowClear placeholder="Filter by country" optionFilterProp="children">
                  {mappedCountries.map(mapping => {
                    const ccode = mapping.c_code;
                    const local = countriesState.value?.[ccode];
                    const label = local ? (local.c_short_name_eng || local.c_full_name_eng || local.c_name_eng) : mapping.geo_area;
                    return (<Option key={ccode} value={ccode}>{ccode} - {label}</Option>);
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="Years (Optional)">
                <DatePicker
                  onlyYearPicker
                  multiple
                  range
                  sort
                  highlightToday={false}
                  placeholder="Select years"
                  value={selectedYears}
                  onChange={setSelectedYears}
                  maxDate={new Date()}
                  minDate={new Date(1970, 0, 1)}
                  plugins={[
                    <DatePanel />,
                    <PresetValuesPlugin position="left" setValue={setSelectedYears} />,
                  ]}
                  style={{ width: '100%', height: '32px', marginTop: '-1px' }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SearchOutlined />}
              loading={loading}
            >
              Convert / Fetch
            </Button>
            <Button
              icon={<ClearOutlined />}
              onClick={() => { form.resetFields(); setData([]); setPagination({ current: 1, pageSize: 100, total: 0, totalPages: 0 }); setSelectedYears([]); setTaskId(null); }}
            >
              Clear
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={exportToCsv}
              loading={exportLoading}
            >
              Export All Data to CSV
            </Button>
          </Space>
          {convertProgress && (
            <div style={{ marginTop: '16px' }}>
              <Progress
                percent={convertProgress.total ? Math.round((convertProgress.progress / convertProgress.total) * 100) : 0}
                status="active"
              />
              <div style={{ textAlign: 'center', marginTop: '4px', color: '#666', fontSize: '12px' }}>
                Fetching WDI data: {convertProgress.progress} / {convertProgress.total} indicators
              </div>
            </div>
          )}
        </Form>
      </Card>

      <Table rowKey={(r)=>r.id || `${r.oic_ind_code}_${r.c_code}_${r.year}`} loading={loading} columns={columns} dataSource={data} pagination={{ current: pagination.current, pageSize: pagination.pageSize, total: pagination.total }} onChange={(p) => handleTableChange(p)} bordered />
    </Space>
  );
}
